import React, {
    MutableRefObject,
    ReactElement, useEffect, useRef, useState,
} from "react";
import {
    GroupBase, NonceProvider, OnChangeValue, OptionsOrGroups, SelectInstance as BaseSelectType,
} from "react-select";
import ReactSelect from "react-select/async";
import { Option, OptionWithChildren, SelectInstanceHandler } from "./Select";

export interface Props<T, TMulti extends boolean = false> {
    placeholder?: string;
    isMulti?: TMulti;
    value?: TMulti extends true ? Option<T>[] : Option<T>;
    onChange?: (options: (TMulti extends true ? readonly Option<T>[] : Option<T>)) => void;
    onBlur?: () => void;
    className?: string;
    isClearable?: boolean;
    onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
    minRequired?: number;
    loadOptions: (initial: string) => Promise<Option<T>[] | OptionWithChildren<T>[]>

}

function getCount(value: unknown[] | unknown | undefined): number {
    if (Array.isArray(value)) {
        return value.length;
    }
    return value ? 1 : 0;
}

const AsyncSelect = React.forwardRef(<T, TMulti extends boolean = false>(
    props: Props<T, TMulti>,
    ref: React.ForwardedRef<SelectInstanceHandler>,
): ReactElement => {
    type Options = OptionsOrGroups<Option<T>, GroupBase<Option<T>>>;

    type SelectInstance = BaseSelectType<Option<T>, TMulti>;
    const [selectedCount, setSelectedCount] = useState(() => getCount(props.value));
    const innerRef = useRef<SelectInstance>();

    const loadOptions = (inputValue: string, callback: (options: Options) => void): void => {
        (async () => {
            try {
                const results = await props.loadOptions(inputValue);
                callback(results as Options);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Error loading options", err);
            }
        })();
    };

    useEffect(() => {
        setSelectedCount(getCount(props.value));
    }, [props.value]);

    useEffect(() => {
        const input = innerRef.current?.inputRef as unknown as HTMLInputElement;
        if (input) {
            input.required = selectedCount < (props.minRequired ?? 0);
        }
    }, [innerRef.current, selectedCount]);

    const onBlur = () => {
        const input = innerRef.current?.inputRef as unknown as HTMLInputElement;
        if (input) {
            input.required = selectedCount < (props.minRequired ?? 0);
        }
        props.onBlur?.();
    };

    const onChange = (val: OnChangeValue<Option<T>, TMulti>) => {
        props.onChange?.(val as (TMulti extends true ? readonly Option<T>[] : Option<T>));
        if (val) {
            setSelectedCount(Array.isArray(val) ? val.length : 1);
        } else {
            setSelectedCount(0);
        }
    };

    const setRef = (value: SelectInstance) => {
        if (ref) {
            // eslint-disable-next-line no-param-reassign
            (ref as MutableRefObject<SelectInstance>).current = value;
        }
        innerRef.current = value;
    };
    return (
        <NonceProvider cacheKey="react-select" nonce={window.NONCE}>
            <ReactSelect
                ref={setRef}
                placeholder={props.placeholder}
                isMulti={props.isMulti}
                onChange={val => onChange(val)}
                onBlur={onBlur}
                isClearable={props.isClearable}
                value={props.value}
                menuPortalTarget={document.body}
                onKeyDown={props.onKeyDown}
                className={props.className}
                styles={{
                    menuPortal: base => ({
                        ...base,
                        zIndex: 1100,
                    }),
                }}
                cacheOptions
                loadOptions={loadOptions}
                defaultOptions
            />
        </NonceProvider>
    );
});

AsyncSelect.defaultProps = {
    placeholder: undefined,
    isMulti: false,
    onChange: undefined,
    onBlur: undefined,
    className: undefined,
    isClearable: false,
    onKeyDown: undefined,
    minRequired: undefined,
    value: undefined,
};

export default AsyncSelect as <
    T,
    TMulti extends boolean = false,
> (
    props: Props<T, TMulti> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof AsyncSelect>;
